import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, Activity, UserPlus, UserMinus, Trash2, ChevronDown, Users } from 'lucide-react';
import { createPost, getPostsByClub, deletePost } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fr } from 'date-fns/locale';
import { formatDistance } from 'date-fns';
import defaultAvatar from '../assets/default-avatar.jpg'

function ClubFeed({ clubId, isMember, clubActivities }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [typePost, setTypePost] = useState('general');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('posts'); // 'posts' ou 'activities'
  const [showTypeSelector, setShowTypeSelector] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    fetchPosts();
  }, [clubId]);

   

  const fetchPosts = async () => {
    try {
      const data = await getPostsByClub(clubId);
      setPosts(data);
    } catch (error) {
      console.error('Erreur lors du chargement des posts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;

    setLoading(true);
    try {
      await createPost(clubId, newPost, typePost);
      setNewPost('');
      setTypePost('general');
      fetchPosts();
    } catch (error) {
      console.error('Erreur lors de la création du post:', error);
      alert(error.response?.data?.message || 'Erreur lors de la publication');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer ce post ?')) return;
    
    try {
      await deletePost(postId);
      fetchPosts();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return d.toLocaleDateString('fr-FR');
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'annonce': return 'bg-red-900/20 border-red-500 text-red-400';
      case 'update': return 'bg-blue-900/20 border-blue-500 text-blue-400';
      default: return 'bg-slate-800 border-slate-700 text-gray-300';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'annonce': return 'Annonce';
      case 'update': return 'Update';
      default: return 'Message';
    }
  };

  const getActivityIcon = (type) => {
    if (type === 'join') {
      return (
        <div className="w-10 h-10 rounded-full bg-green-900/30 border border-green-500 flex items-center justify-center">
          <Users className="text-green-500" size={20} />
        </div>
      );
    } else {
      return (
        <div className="w-10 h-10 rounded-full bg-red-900/30 border border-red-500 flex items-center justify-center">
          <Users className="text-red-500" size={20} />
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-950">
      {/* Header avec onglets - Fixed en haut */}
      <div className="sticky top-0 z-10 bg-slate-950 border-b border-slate-800">
        <div className="flex">
          <button
            onClick={() => setActiveTab('posts')}
            className={`flex-1 py-4 px-4 font-semibold transition-all flex items-center justify-center space-x-2 ${
              activeTab === 'posts'
                ? 'border-b-2 border-indigo-500 text-indigo-500 bg-indigo-500/5'
                : 'text-gray-400'
            }`}
          >
            <MessageSquare size={20} />
            <span>Messages</span>
          </button>
          <button
            onClick={() => setActiveTab('activities')}
            className={`flex-1 py-4 px-4 font-semibold transition-all flex items-center justify-center space-x-2 ${
              activeTab === 'activities'
                ? 'border-b-2 border-indigo-500 text-indigo-500 bg-indigo-500/5'
                : 'text-gray-400'
            }`}
          >
            <Activity size={20} />
            <span>Activité</span>
          </button>
        </div>
      </div>

      {/* Zone de messages scrollable */}
      <div className="flex-1 overflow-y-auto px-4 py-4 max-h-[60vh]">
        {activeTab === 'posts' ? (
          <div className="space-y-3 pb-4">
            {posts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <MessageSquare size={48} className="text-slate-700 mb-4" />
                <p className="text-gray-400">Aucun message pour le moment</p>
                <p className="text-gray-500 text-sm mt-1">Soyez le premier à poster !</p>
              </div>
            ) : (
              <>
                {posts.map((post) => (
                  <div 
                    key={post._id} 
                    className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-4 hover:border-slate-700 transition-all"
                  >
                    {/* Header du message */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1">
                        <img
                          src={post.auteur?.photoProfil || defaultAvatar}
                          alt={`${post.auteur?.prenom} ${post.auteur?.nom}`}
                          className="w-10 h-10 rounded-full object-cover border-2 border-slate-800"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="font-semibold text-white text-sm truncate">
                              {post.auteur?.prenom} {post.auteur?.nom}
                            </p>
                            {post.typePost !== 'general' && (
                              <span className={`px-2 py-0.5 border rounded-full text-xs font-medium ${getTypeColor(post.typePost)}`}>
                                {getTypeLabel(post.typePost)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{formatDate(post.createdAt)}</p>
                        </div>
                      </div>
                      
                      {user && (post.auteur._id === user._id || user.role === 'admin') && (
                        <button
                          onClick={() => handleDelete(post._id)}
                          className="text-red-400/70 hover:text-red-400 transition ml-2 p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>

                    {/* Contenu du message */}
                    <p className="text-gray-200 text-[15px] leading-relaxed whitespace-pre-wrap pl-[52px]">
                      {post.contenu}
                    </p>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>
        ) : (
          // Onglet Activités
          <div className="space-y-2 pb-4">
            {clubActivities && clubActivities.length > 0 ? (
              clubActivities.map((activity, index) => (
                <div 
                  key={index} 
                  className="bg-slate-900/30 backdrop-blur-sm border border-slate-800/50 rounded-xl p-3 hover:border-slate-700 transition-all"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-300 text-sm">
                        <span className="font-semibold text-white">
                          {activity.user?.prenom} {activity.user?.nom}
                        </span>
                        {activity.type === 'join' ? (
                          <span className="text-green-400"> a rejoint le club</span>
                        ) : (
                          <span className="text-red-400"> a quitté le club</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{formatDate(activity.date)}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Activity size={48} className="text-slate-700 mb-4" />
                <p className="text-gray-400">Aucune activité récente</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Zone de saisie - Fixed en bas (seulement pour l'onglet messages) */}
      {activeTab === 'posts' && isMember && (
        <div className="sticky bottom-0 bg-slate-950 border-t border-slate-800 p-4">
          {/* Sélecteur de type (apparaît au-dessus) */}
          {showTypeSelector && (
            <div className="mb-3 flex gap-2 animate-in slide-in-from-bottom-2">
              {['general', 'update', 'annonce'].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setTypePost(type);
                    setShowTypeSelector(false);
                    textareaRef.current?.focus();
                  }}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    typePost === type
                      ? 'bg-indigo-500 text-white'
                      : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                  }`}
                >
                  {getTypeLabel(type)}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            {/* Bouton pour changer le type */}
            <button
              type="button"
              onClick={() => setShowTypeSelector(!showTypeSelector)}
              className="flex-shrink-0 h-11 w-11 flex items-center justify-center bg-slate-800 hover:bg-slate-700 text-gray-400 rounded-xl transition-all"
            >
              <ChevronDown 
                size={20} 
                className={`transition-transform ${showTypeSelector ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Zone de texte */}
            <div className="flex-1 min-h-[44px] bg-slate-800 rounded-2xl border border-slate-700 focus-within:border-indigo-500 transition-all flex items-center">
              <textarea
                ref={textareaRef}
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                placeholder="Message..."
                className="w-full px-4 py-3 bg-transparent text-white placeholder-gray-500 resize-none focus:outline-none text-base antialiased [-webkit-font-smoothing:antialiased] [-moz-osx-font-smoothing:grayscale]"
                rows="1"
                maxLength="1000"
                style={{
                  minHeight: '44px',
                  maxHeight: '120px',
                  height: '44px'
                }}
                onInput={(e) => {
                  e.target.style.height = '44px';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
              />
              {newPost.length > 900 && (
                <p className="text-xs text-gray-500 px-4 pb-2 absolute bottom-0 right-16">
                  {newPost.length}/1000
                </p>
              )}
            </div>

            {/* Bouton d'envoi */}
            <button
              type="submit"
              disabled={loading || !newPost.trim()}
              className="flex-shrink-0 h-11 w-11 flex items-center justify-center bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-all disabled:bg-slate-800 disabled:text-gray-600 shadow-lg shadow-indigo-500/20 disabled:shadow-none"
            >
              <Send size={20} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default ClubFeed;